export type Nullable<T> = T | null;

export enum AdminRoles {
  MasterAdmin = 1,
  AEContentAdmin = 2,
  WorkspaceAdmin = 3,
}

export enum WorkspaceRoles {
  WorkspaceAdmin = 3,
  WorkspaceUser = 4,
  WorkspaceViewer = 5,
}

export enum Roles {
  WorkspaceUser = 4,
  WorkspaceViewer = 5,
}

export enum ActionType {
  Login = 'LOGIN',
  Signup = 'SIGNUP',
}

export const roleOptions = [
  { label: 'Workspace Admin', value: AdminRoles.WorkspaceAdmin },
  { label: 'Workspace Contributor', value: Roles.WorkspaceUser },
  { label: 'Workspace Viewer', value: Roles.WorkspaceViewer },
];

// roleOptions shaped for SimpleSelect (string values).
export const roleSelectItems = roleOptions.map((o) => ({ label: o.label, value: String(o.value) }));

// External OIDC SSO. Mirrors the backend models.SSOConfigResponse. The client
// secret is never returned — has_client_secret reports whether one is stored.
export interface SSOConfig {
  id: number;
  workspace_id: number;
  slug: string;
  provider: string;
  issuer_url: string;
  client_id: string;
  has_client_secret: boolean;
  default_role_id: number;
  group_role_mappings: Record<string, number>;
  allowed_domains: string[];
  enabled: boolean;
  login_url: string;
  created_at: string;
  updated_at: string;
}

// Mirrors backend models.UpsertSSOConfigRequest. client_secret is optional on
// update (omit to keep the stored one). The login slug is NOT sent: the backend
// derives it from the workspace's reference_id, so it is unique and immutable.
export interface UpsertSSOConfigRequest {
  issuer_url: string;
  client_id: string;
  client_secret?: string;
  default_role_id: number;
  group_role_mappings?: Record<string, number>;
  allowed_domains?: string[];
  enabled?: boolean;
}

export enum BlockType {
  Text = 1,
  Numeric = 2,
  Date = 3,
  Selection = 4,
  Media = 5,
  Radio = 8,
  LongText = 9,
  Checkbox = 10,
  Section = 12,
  Link = 13,
  SectionTitle = 14,
}

export enum SelectionType {
  Single = 'single',
  Multi = 'multi',
}

export enum DateType {
  Date = 'date',
  Datetime = 'datetime',
}

export enum StaticField {
  Name = 's_name',
  Description = 's_description',
  Media = 's_icon',
  IsPublic = 's_is_public',
  CreatedDate = 's_created_date',
  UpdatedDate = 's_updated_date',
}
export interface BlockResponseObject {
  [blockId: string]: any;
}

export interface Block {
  id: number;
  referenceID: string;
  field_id: string;
  field_name: string;
  field_unique_name: string;
  workspace_id: number;
  content_type_id: number;
}

export interface TextBlock extends Block {
  field_type: BlockType.Text;
  properties: {
    maxlength: number;
    minlength: number;
    placeholder?: string;
    showAsMetaPill?: boolean;
  };
}

export interface NumberBlock extends Block {
  field_type: BlockType.Numeric;
  properties?: {
    showAsMetaPill?: boolean;
  };
}

export interface DateBlock extends Block {
  field_type: BlockType.Date;
  properties: {
    dateType: DateType.Date | DateType.Datetime;
    showInCalendarView: boolean;
    showAsMetaPill?: boolean;
  };
}

export interface SelectionBlock extends Block {
  field_type: BlockType.Selection;
  properties: {
    selectionType: SelectionType.Single | SelectionType.Multi;
    options: Array<{ label: string; value: string }>;
    showAsMetaPill?: boolean;
  };
}

export interface LongTextBlock extends Block {
  field_type: BlockType.LongText;
  ai_prompt: any;
}

export interface SectionBlock extends Block {
  field_type: BlockType.Section;
}

export interface MediaBlock extends Block {
  field_type: BlockType.Media;
}

export interface CheckboxBlock extends Block {
  field_type: BlockType.Checkbox;
}

export interface LinkBlock extends Block {
  field_type: BlockType.Link;
}

export interface LinkBlockPayload {
  value: string;
  text?: string;
}

export interface SectionTitleBlock extends Block {
  field_type: BlockType.SectionTitle;
}

export type AnyBlock =
  | SelectionBlock
  | TextBlock
  | NumberBlock
  | DateBlock
  | CheckboxBlock
  | LongTextBlock
  | MediaBlock
  | SectionBlock
  | LinkBlock
  | SectionTitleBlock;

export type InputBlock =
  | SelectionBlock
  | TextBlock
  | NumberBlock
  | DateBlock
  | CheckboxBlock
  | LongTextBlock
  | MediaBlock
  | SectionBlock
  | LinkBlock
  | SectionTitleBlock;

export interface SelectOptionType<T = string> {
  label: string;
  value: T;
}

export interface Row {
  id: number;
  created_at: string;
  updated_at: string;
}

export interface User extends Row {
  email: string;
  first_name: string;
  last_name: string;
  invitation_link: string;
  default_workspace_id: number;
  is_active: boolean;
  role_id: number;
  role_name: string;
  has_payment_methods: boolean;
  is_invitation_sent: boolean;
  config: {
    share_content_email?: boolean;
    join_workspace_email?: boolean;
    comment_email?: boolean;
  };
}

export interface Workspace extends Row {
  name: string;
  description: string;
  country?: string;
  stripe_customer_id: Nullable<string>;
  plan_id: number;
  plan_start_date: string;
  plan_end_date: string;
  has_payment_methods: boolean;
  sport_type_id: number;
  is_base: boolean;
  is_public: boolean;
  primary_color: string;
  secondary_color: string;
  logo: string;
  banner_logo: string;
  banner_image: string;
}

export interface ExtendedWorkspace extends Workspace {
  role_id: number;
  role_name: string;
}

export interface WorkspaceUser extends Row {
  workspace_id: number;
  user_id: number;
  role_id: number;
  invitation_token: string;
}

export interface WorkspacePayment extends Row {
  workspace_id: number;
  user_id: number;
  payment: number;
}

export interface Plan extends Row {
  name: string;
  stripe_product_id: string;
}

export interface Role {
  id: number;
  name: string;
}

export interface Feature {
  name: string;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  active: boolean;
  deleted: boolean;
  livemode: boolean;
  object: string;
  package_dimensions: string;
  shippable: boolean;
  statement_descriptor: string;
  tax_code: string;
  type: string;
  unit_label: string;
  url: string;
  marketing_features: Feature[];
  default_price: Price;
  price: Nullable<Price>;
  created: number;
  updated: number;
}

export interface Price {
  id: string;
  active: boolean;
  billing_scheme: string;
  currency: string;
  currency_options: Nullable<string>;
  custom_unit_amount: Nullable<string>;
  deleted: boolean;
  livemode: boolean;
  lookup_key: string;
  metadata: Nullable<object>;
  nickname: string;
  object: string;
  tax_behavior: string;
  tiers: Nullable<string>;
  tiers_mode: '';
  transform_quantity: Nullable<string>;
  type: string;
  unit_amount: number;
  unit_amount_decimal: 'number';
  usage_count: number;
  created: number;
}

export interface Action {
  cnt: number;
  workspace_id: number;
  action_type: string;
  action_date: string;
}

export interface ContentType extends Row {
  name: string;
  description: string;
  icon: string;
  sport_type_id: number;
  workspace_id: number;
  created_by: number;
  is_public: boolean;
  public_link: string;
  reference_id: string;
  workspace_content_type_reference_id: string;
  sample_image_url?: string;
}

export interface Field<T = Record<string, unknown>> extends Row {
  name: string;
  field_type: number;
  workspace_id: number;
  unique_name: string;
  type: number;
  properties: T & {
    minlength: number;
    maxlength: number;
    selectionType: SelectionType.Single | SelectionType.Multi;
    dateType: DateType.Date | DateType.Datetime;
    showInCalendarView: boolean;
    showAsMetaPill?: boolean;
  };
}

export interface FieldOption extends Row {
  field_id: number;
  workspace_id: number;
  option_label: string;
  option_value: string;
  is_default: boolean;
  sort_order: number;
}

export interface ContentTypeField extends Row {
  field_id: number;
  workspace_id: number;
  content_type_id: number;
}

export interface ContentTypeFieldView extends Row {
  workspace_id: number;
  content_type_id: number;
  field_id: number;
  field_name: string;
  field_type: number;
  field_unique_name: string;
  properties: any;
  sort_order: number;
  referenceID: string;
  field_options: FieldOption[];
}

export interface Section extends Row {
  name: string;
  description: string;
  duration: number;
  content_type_id: number;
  workspace_id: number;
}

export interface Content extends Row {
  workspace_id: number;
  content_type_id: number;
  sport_type_id: number;
  is_public: boolean;
  public_link: string;
  content: any;
  status: string;
  reference_id: string;
  name: string;
  description: string;
  icon: string;
  thumbnail: string;
  content_type_name: string;
  media_files: any;
  workspace: string;
  created_user: string;
  // Present only on library-wide search hits that come from a partner workspace.
  workspace_name?: string;
}

export enum FileType {
  Image = 'image',
  Video = 'video',
}

export interface File {
  type: FileType.Image | FileType.Video;
  src: string;
}

export interface GroupForm {
  name: string;
  contents: string[];
}

export interface SectionForm {
  name: string;
  duration: number | null | string;
  notes: string;
  groups: GroupForm[];
  equipment?: string[];
  setup?: string[];
  instructions?: string[];
  keyPoints?: string[];
  variations?: string[];
}

export interface SportType {
  id: number;
  name: string;
}

export interface Favorite {
  id: number;
  user_id: number;
  workspace_content_id: number;
  created_id: string;
}

export interface ContentCount {
  content_type_id: number;
  content_type_name: string;
  count: number;
}

export interface WorkspaceContentType extends Row {
  workspace_id: number;
  content_type_id: number;
}

export interface DailogProps {
  isOpen: boolean;
  onClose(): void;
}

export interface ApiKey {
  id: number;
  workspace_id: number;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at?: string | null;
  last_used_ip?: string | null;
  revoked_at?: string | null;
  expires_at?: string | null;
  created_at: string;
  created_by_user_id?: number;
  created_by_name?: string | null;
}

export interface CreatedApiKey {
  api_key: ApiKey;
  key: string;
}

export interface ApiKeyAuditEvent {
  id: number;
  api_key_id: number;
  workspace_id: number;
  event_type: string;
  actor_user_id?: number | null;
  actor_name?: string | null;
  ip?: string | null;
  metadata: Record<string, unknown>;
  occurred_at: string;
}

export interface CreateApiKeyInput {
  name: string;
  scopes?: string[];
  expires_at?: string | null;
}

export interface UpdateApiKeyInput {
  name?: string;
  scopes?: string[];
}

export interface Author {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  workspace_id: number;
}

export interface CollectionType extends Row {
  name: string;
  description: string;
  reference_id: string;
  workspace_id: number;
  workspace_reference_id: string;
  public_link: string;
}

export interface CollectionContentType extends Row {
  content_type_id: number;
  workspace_id: number;
  collection_type_id: number;
}

export interface Collection extends Row {
  name: string;
  description: string;
  collection_type_id: number;
  workspace_id: number;
  reference_id: string;
  content: any;
  status: string;
  created_by: number;
  public_link: string;
}

export interface Event extends Row {
  title: string;
  description: string;
  workspace_id: number;
  reference_id: string;
  contents: any;
  status: string;
  created_by: number;
  event_time: string;
  event_date: string;
}

export interface Comment extends Row {
  workspace_content_id: number;
  user_id: number;
  comment: string;
}

export interface Document extends Row {
  key: string;
  url: string;
  size: number;
  last_modified: string;
  vectorized: boolean;
}

export interface ContentName extends Row {
  name: string;
  description: string;
  workspace_id: number;
  sport_type: string;
  country: string;
  age_level: string;
  content_type: string;
  s3_bucket: string;
  s3_key: string;
  doc_version: string;
  source_file_id: string;
}

export interface AIContentRequest extends Row {
  workspace_id: number;
  country: string;
  sport_type: string;
  content_type_id: number;
  name: string;
  description: string;
  created_by: number;
  creation_result: string;
  created_description: string;
  content_type: string;
  sections: any;
  created_content: any;
  ai_prompt_content?: string;
  ai_prompt_image?: string;
  sources?: {
    chunks?: string[];
    documents?: string[];
    synthesized_answer?: string;
  };
}

// Gemini Knowledge Base Types
export type KnowledgeBaseDocumentStatus = 'pending' | 'processing' | 'ready' | 'error';

export interface KnowledgeBaseDocument extends Row {
  workspace_id: number;
  content_type_id?: number;
  document_name: string;
  original_filename: string;
  mime_type: string;
  file_size_bytes: number;
  status: KnowledgeBaseDocumentStatus;
  error_message?: string;
  gemini_file_id?: string;
  gemini_operation_name?: string;
  file_search_store_id?: string;
  file_search_store_name?: string;
  uploaded_by: number;
}

export interface FileSearchStore extends Row {
  workspace_id: number;
  content_type_id?: number;
  store_name: string;
  display_name: string;
}

export interface GeminiDocument {
  name: string;
  displayName?: string;
  createTime?: string;
  updateTime?: string;
  sizeBytes?: string;
  mimeType?: string;
  sha256Hash?: string;
  uri?: string;
  state?: string;
}

export interface RetrievedContext {
  text: string;
  title: string;
  uri: string;
}

export interface GroundingChunk {
  retrieved_context: RetrievedContext;
}

export interface Segment {
  start_index: number;
  end_index: number;
  text: string;
}

export interface GroundingSupport {
  segment: Segment;
  grounding_chunk_indices: number[];
}

export interface KnowledgeBaseQueryResult {
  answer: string;
  grounding_chunks?: GroundingChunk[];
  grounding_support?: GroundingSupport[];
  metadata?: Record<string, unknown>;
}

// --- Planner ---------------------------------------------------------------
// Shared JSONB document shape stored on practice_plans.sections and
// curriculum_plans.sections. Mirrors the Go `PlanSections` struct — keep
// field names in sync with the backend, snake_case throughout.

export interface PlanItem {
  uid: string;
  name: string;
  content_id: Nullable<number>;
  content_reference_id: Nullable<string>;
  content_type_id: Nullable<number>;
  content_type_name: Nullable<string>;
  custom_label: Nullable<string>;
}

export interface PlanSection {
  uid: string;
  name: string;
  duration_minutes: number;
  notes: string;
  items: PlanItem[];
}

// 'workspace' → the team's shared schedule: at most one per (workspace, date),
// visible to every member including viewers. 'personal' → "My Plan": at most
// one per (workspace, date, user), visible only to its creator. Fixed at
// creation — the API ignores scope on update.
export type PlanScope = 'workspace' | 'personal';

export interface PlannerPlan extends Row {
  workspace_id: number;
  scope: PlanScope;
  name: string;
  plan_date: string; // 'YYYY-MM-DD'
  start_time: string; // 'HH:mm'
  duration_minutes: number;
  goals: string;
  notes: string;
  // Point-in-time snapshot inherited from the parent Curriculum at copy time (or
  // typed by hand on a standalone plan). A scheduled day plan is publicly
  // shareable and must keep rendering the text it was built with, so these stay
  // on the plan even after the curriculum is edited.
  learning_outcomes: string;
  prerequisites: string;
  sections: PlanSection[];
  from_curriculum: boolean;
  source_curriculum_plan_id: Nullable<number>;
  reference_id: string;
  created_by: number;
  // Computed server-side and relative to the requesting user: personal plans
  // are editable only by their creator; for workspace plans admins may edit
  // any plan, a contributor may only edit plans authored by non-admins, and
  // viewers may edit none. False on the public share payload. The API
  // enforces the same rule — this only drives the UI.
  can_edit: boolean;
}

export interface Curriculum extends Row {
  workspace_id: number;
  name: string;
  description: string;
  // Authored once on the curriculum, not per slot: prerequisites are what an
  // athlete needs *before starting* the curriculum, learning outcomes what they
  // know *after finishing* it — both describe the whole curriculum. Day plans
  // copied from a slot snapshot these onto themselves (see PlannerPlan).
  learning_outcomes: string;
  prerequisites: string;
  source: 'workspace' | 'partner';
  reference_id: string;
  created_by: number;
  plan_count: number;
  built_count: number;
  plans?: CurriculumPlan[];
}

// A slot in a curriculum. No learning_outcomes/prerequisites here — those live
// on the parent Curriculum (see above), since they describe the curriculum as a
// whole rather than one session.
export interface CurriculumPlan extends Row {
  curriculum_id: number;
  workspace_id: number;
  name: string;
  sort_order: number;
  duration_minutes: number;
  goals: string;
  notes: string;
  sections: PlanSection[];
  reference_id: string;
  // Derived server-side: true once anything is authored into the slot —
  // goals, notes, or a content item. Rendered as Complete/Incomplete on the
  // admin build screen; gates "Add to date" on the coach picking screen.
  is_built: boolean;
}
// --- Courses ---------------------------------------------------------------
// Mirrors baseapi/app/courses/entities. Unlike Planner, course items are real
// rows (course_items), not a JSONB document — completion records FK to them.
// Keep field names in sync with the backend, snake_case throughout.

export type CourseStatus = 'draft' | 'published' | 'archived';

// 'login' → the shared link asks for a sign-in before progress is recorded.
// 'open'  → anyone holding the link may complete the course anonymously.
export type CourseAccessMode = 'login' | 'open';

// 'individual' → one completion row per (user, item); backs the per-learner
// roster. 'count_only' → anonymous aggregate counters. Fixed once the course
// has learner data — progress or an assignment (tracking_locked) — the API
// answers 409 on a change attempt. Sharing alone does not lock.
export type CourseTrackingMode = 'individual' | 'count_only';

export interface CourseItem extends Row {
  module_id: number;
  course_id: number;
  workspace_id: number;
  // Null when the source library content has been deleted: the item row (and
  // everyone's completions) survive, rendered from the snapshot labels below.
  content_id: Nullable<number>;
  content_reference_id: Nullable<string>;
  content_type_id: Nullable<number>;
  content_type_name: string;
  content_name: string;
  source_label: string;
  sort_order: number;
  // Caller-relative annotations from the progress service.
  is_completed: boolean;
  completed_at?: Nullable<string>;
  is_locked: boolean;
}

export interface CourseModule extends Row {
  course_id: number;
  workspace_id: number;
  name: string;
  // "How to complete this module" — shown to the learner above the content.
  instructions: string;
  // Optional off-platform step the learner confirms before the next module
  // unlocks; empty string means no assignment.
  assignment_text: string;
  assignment_shortcut: string;
  sort_order: number;
  reference_id: string;
  items?: CourseItem[];
  // Caller-relative annotations from the progress service. The API enforces
  // the gate; these only drive the UI.
  is_locked: boolean;
  is_completed: boolean;
  assignment_acked: boolean;
}

export interface Course extends Row {
  workspace_id: number;
  name: string;
  description: string;
  audience_type: string;
  estimated_minutes: number;
  cover_image: string;
  status: CourseStatus;
  access_mode: CourseAccessMode;
  tracking_mode: CourseTrackingMode;
  tracking_locked: boolean;
  shared_at: Nullable<string>;
  // Set while the course is listed in the cross-workspace Community Hub.
  community_shared_at: Nullable<string>;
  // Currently published version, bumped on every publish transition. Zero means
  // never published. Admin-facing only — learners never see a version number.
  version: number;
  reference_id: string;
  created_by: number;
  modules?: CourseModule[];
  // Filled from a single aggregate query on list responses.
  module_count: number;
  item_count: number;
  // Computed server-side and relative to the requesting user — same contract
  // as PlannerPlan.can_edit. The API enforces the rule; this drives the UI.
  can_edit: boolean;
  // Requesting learner's progress, populated on annotated reads.
  completed_item_count: number;
  // When the learner finished the course — assigned-list reads only, present
  // only once every item is complete.
  completed_at?: Nullable<string>;
  // Workspace branding, present only on the public share payload. The
  // Community Hub listing also fills workspace_name (the source org label).
  workspace_name?: string;
  workspace_logo?: string;
  workspace_primary_color?: string;
  // Sharing workspace's sport, present only on the Community Hub listing.
  sport_type_name?: string;
  // Author display name, present on workspace list reads (the hub's chip).
  created_by_name?: string;
  // Mirrors course_reminders.due_date on learner-facing reads ('YYYY-MM-DD');
  // absent when no due date is set.
  due_date?: Nullable<string>;
  // Where the reminder schedule stands today; absent when the cadence is
  // disabled or the season hasn't reached its first stop.
  pace?: CoursePace;
  // Hub-list completion column, filled on workspace list responses only.
  // Individually tracked courses count people; count-only courses count
  // anonymous sessions (assigned_count stays 0).
  assigned_count: number;
  started_count: number;
  completed_learner_count: number;
}

// The schedule stop the season calendar is currently on — backs the learner
// "Week 3 — you should be on X" banner.
export interface CoursePace {
  module_id: number;
  module_name: string;
  position: number;
  total_modules: number;
  date: string;
  // When this learner was last nudged about this course; absent if never.
  last_reminder_at?: string;
}

// --- Workspace groups --------------------------------------------------------
// Mirrors baseapi/app/groups/entities. auto_role_id set = role-backed group
// (membership follows the workspace role, resolved at read time); null =
// custom group with explicit members.

export interface GroupMember {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
}

export interface WorkspaceGroup extends Row {
  workspace_id: number;
  name: string;
  description: string;
  auto_role_id: Nullable<number>;
  reference_id: string;
  created_by: number;
  member_count: number;
  members?: GroupMember[];
}

export interface CourseAssignment {
  id: number;
  course_id: number;
  workspace_id: number;
  assignee_type: 'user' | 'group';
  user_id: Nullable<number>;
  group_id: Nullable<number>;
  assigned_by: number;
  created_at: string;
  // Resolved server-side: person name/email or group name; groups carry a
  // live effective member count.
  label: string;
  member_count: number;
}

export interface CourseLearnerMetrics {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  completed_item_count: number;
  started_at: Nullable<string>;
  completed_at: Nullable<string>;
  // One entry per module in walk order — the roster's progress dots.
  modules_completed: boolean[];
  // Workspace role name without its "Workspace " prefix: Admin | Contributor |
  // Viewer.
  role: string;
  // Course version this learner is pinned to — the version recorded on their
  // earliest completion. Zero until they start.
  version: number;
  // True when the reminder schedule has passed more modules than the learner
  // has finished. Always false when the course has no enabled cadence.
  is_behind: boolean;
  // True when the course due date has passed and this learner hasn't
  // finished. Independent of is_behind; always false without a due date.
  is_overdue: boolean;
}

// Per-module completion across the roster — the "37 of 42" bar on each module
// in the course overview. Individually tracked courses only.
export interface CourseModuleMetrics {
  module_id: number;
  name: string;
  item_count: number;
  completed_count: number;
}

// Shape switches on tracking_mode: individually tracked courses carry the
// learner roster; count-only courses carry anonymous counters only.
export interface CourseMetrics {
  tracking_mode: CourseTrackingMode;
  item_count: number;
  module_count: number;
  assigned_count: number;
  started_count: number;
  completed_count: number;
  learners?: CourseLearnerMetrics[];
  // How many learners are behind the reminder pace. Zero when the course has no
  // enabled cadence — with no pace to keep, nobody is behind it.
  behind_count: number;
  // The due date the overdue flags are measured against ('YYYY-MM-DD') and how
  // many learners passed it unfinished. Zero without a due date.
  due_date?: Nullable<string>;
  overdue_count: number;
  modules?: CourseModuleMetrics[];
}

// One immutable publish event. The stored tree snapshot stays off the history
// list — the single-version read (GET .../versions/:n) carries it as
// snapshot_modules for inspection.
export interface CourseVersion {
  id: number;
  course_id: number;
  workspace_id: number;
  version: number;
  note: string;
  published_by: number;
  created_at: string;
  published_by_name: string;
  // How many learners are pinned to this version.
  learner_count: number;
  module_count: number;
  item_count: number;
  // Present on the single-version read only.
  snapshot_modules?: CourseModule[];
}

// Spacing between module reminders.
export type Cadence = 'weekly' | 'biweekly' | 'monthly';

// One module's slot on the season calendar, derived server-side from
// season_start + cadence over the course's modules — never stored.
export interface ReminderStop {
  module_id: number;
  name: string;
  position: number;
  // Calendar date, 'YYYY-MM-DD'. Date-only by design: a season runs on the
  // coach's calendar, so no timezone is attached.
  date: string;
  // True once the date has passed — the learners who haven't reached this
  // module by now are the ones counted as behind.
  passed: boolean;
}

// A course's reminder cadence. Absent server-side reads as disabled, so the
// GET always answers a row rather than 404.
export interface CourseReminders {
  course_id: number;
  workspace_id: number;
  enabled: boolean;
  // Calendar dates, 'YYYY-MM-DD'. Required when enabled; due_date is optional.
  season_start: Nullable<string>;
  cadence: Cadence;
  due_date: Nullable<string>;
  updated_by: number;
  schedule?: ReminderStop[];
}

// ---- OAuth connections (MCP connectors for Claude / ChatGPT) ----------------

export interface OAuthConsentClient {
  client_id: string;
  /** The trustworthy identifier: proven by fetch for verified clients. */
  host: string;
  /** Self-reported for unverified (dynamically registered) clients. */
  name?: string;
  logo_uri?: string;
  type: string;
  verified: boolean;
}

export interface OAuthConsentScope {
  scope: string;
  description: string;
  write: boolean;
}

export interface OAuthConsentWorkspace {
  id: number;
  reference_id: string;
  name: string;
  role_id: number;
  role_name: string;
  /** Tells apart workspaces that share a name; absent when unknown. */
  sport_type?: string;
  is_default: boolean;
}

export interface OAuthConsentDetails {
  request_id: string;
  client: OAuthConsentClient;
  redirect_host: string;
  scopes: OAuthConsentScope[];
  workspaces: OAuthConsentWorkspace[];
  expires_at: string;
}

export interface OAuthDecisionRequest {
  decision: 'approve' | 'deny';
  workspace_id?: number;
  scopes?: string[];
}

export interface OAuthGrant {
  id: number;
  client_id: string;
  client_host: string;
  client_name?: string;
  verified: boolean;
  scopes: string[];
  default_workspace_id: number;
  default_workspace_name?: string;
  created_at: string;
  last_authorized_at: string;
  last_used_at?: string | null;
}

// ---- Assistant uploads (upload links an assistant creates over MCP) --------

export type AssistantUploadTarget = 'content' | 'course_cover';
export type AssistantMediaKind = 'image' | 'video';

export interface AssistantUploadedFile {
  kind: AssistantMediaKind;
  file_url: string;
  /** Empty for course covers, which are images themselves. */
  thumbnail_url: string;
}

export interface AssistantUpload {
  target: AssistantUploadTarget;
  /** The content item's or course's name. */
  target_name: string;
  accepts: AssistantMediaKind[];
  max_image_bytes: number;
  /** Absent when the target takes no videos (course covers). */
  max_video_bytes?: number;
  /** Total files the link can add (content), or 1 for a cover, which each upload replaces. */
  max_files: number;
  expires_at: string;
  /** Seconds left when the API answered; unlike expires_at, immune to clock skew. */
  expires_in: number;
  /** Files added through this link so far, oldest first. */
  uploaded: AssistantUploadedFile[];
}
